import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, ArrowRight, Check, Smartphone, ChefHat, CreditCard, BarChart3, Phone, Mail, Lock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
import { ASTERPOS_LOGO } from '@/config/brandAssets';

export default function OwnerSignupPage() {
  const navigate = useNavigate();
  // the plan picked on the pricing page (?plan=GROWTH) is preselected at the billing step
  useEffect(() => {
    const plan = new URLSearchParams(window.location.search).get('plan');
    if (plan) {
      try { localStorage.setItem('chain29_signup_plan', plan.toUpperCase()); } catch { /* private window */ }
    }
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    smsOptIn: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (!form.smsOptIn) {
      setError('SMS consent is required to continue');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/api/public/onboarding/restaurant/owner/signup/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          phone: form.phone,
          password: form.password,
          sms_opt_in: form.smsOptIn
        })
      });
      
      const data = await response.json();
      
      if (data.ok) {
        toast.success('Account created! Check your email and phone for next steps.');
        // Store signup_id for continuation
        localStorage.setItem('owner_signup_id', data.signup_id);
        // Navigate to continue page (will be implemented in next phase)
        navigate(`/owner/signup/continue?signup_id=${data.signup_id}`);
      } else {
        setError(data.message || 'Signup failed');
        toast.error(data.message || 'Signup failed');
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError('An error occurred. Please try again.');
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Smartphone, title: 'Modern POS', desc: 'Cloud-based point of sale that works on any device' },
    { icon: ChefHat, title: 'Kitchen Display', desc: 'Keep your kitchen in sync with real-time orders' },
    { icon: CreditCard, title: 'Payments', desc: 'Accept all payment types with competitive rates' },
    { icon: BarChart3, title: 'Analytics', desc: 'Insights to grow your business' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={ASTERPOS_LOGO} alt="Chain29" className="h-7 w-7 sm:h-8 sm:w-8" />
            <span className="text-lg sm:text-xl font-bold text-white">Chain29</span>
          </Link>
          <Link to="/owner/login">
            <Button variant="ghost" className="text-white hover:text-white hover:bg-white/10 text-sm sm:text-base min-h-[44px]">
              <span className="hidden sm:inline">Already have an account?</span> Login
            </Button>
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-12">
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
          {/* Left: Features - Hidden on very small screens in favor of form */}
          <div className="space-y-4 sm:space-y-8 lg:order-1 order-2">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-2 sm:mb-4">
                Start Your Restaurant with Chain29
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-slate-300">
                Everything you need to run your restaurant. POS, kitchen display, payments, and more.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              {features.map((feature, i) => (
                <div key={i} className="flex items-start gap-2 sm:gap-3 p-2 sm:p-4 rounded-lg bg-white/5 border border-white/10">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-orange-500/20 flex-shrink-0">
                    <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-white text-sm sm:text-base">{feature.title}</h3>
                    <p className="text-xs sm:text-sm text-slate-400 line-clamp-2">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 text-slate-400 text-sm sm:text-base">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 flex-shrink-0" />
              <span>7-day free trial • Nothing charged until it ends</span>
            </div>
          </div>

          {/* Right: Signup Form */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 lg:order-2 order-1">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-xl sm:text-2xl text-white">Create Your Account</CardTitle>
              <CardDescription className="text-sm sm:text-base text-slate-300">
                Set up your restaurant in minutes
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}
                
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="email" className="text-white text-sm sm:text-base">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="owner@restaurant.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="pl-10 h-11 sm:h-10 text-base bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      required
                      data-testid="owner-signup-email"
                    />
                  </div>
                </div>
                
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="phone" className="text-white text-sm sm:text-base">Cell Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(415) 555-1234"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="pl-10 h-11 sm:h-10 text-base bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      required
                      data-testid="owner-signup-phone"
                    />
                  </div>
                </div>
                
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="password" className="text-white text-sm sm:text-base">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <PasswordInput
                      id="password"
                      placeholder="••••••••"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="pl-10 h-11 sm:h-10 text-base bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      required
                      minLength={8}
                      data-testid="owner-signup-password"
                    />
                  </div>
                </div>
                
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="confirmPassword" className="text-white text-sm sm:text-base">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <PasswordInput
                      id="confirmPassword"
                      placeholder="••••••••"
                      value={form.confirmPassword}
                      onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                      className="pl-10 h-11 sm:h-10 text-base bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      required
                      minLength={8}
                      data-testid="owner-signup-confirm-password"
                    />
                  </div>
                </div>

                {/* SMS Consent Checkbox */}
                <div className="flex items-start space-x-3 p-3 sm:p-4 rounded-lg bg-white/5 border border-white/10">
                  <Checkbox
                    id="smsOptIn"
                    checked={form.smsOptIn}
                    onCheckedChange={(checked) => setForm({ ...form, smsOptIn: checked })}
                    className="mt-0.5 border-white/40 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500 h-5 w-5"
                    data-testid="owner-signup-sms-consent"
                  />
                  <Label htmlFor="smsOptIn" className="text-xs sm:text-sm text-slate-300 leading-relaxed cursor-pointer">
                    I agree to receive SMS messages about my account setup. Message and data rates may apply. Reply STOP to opt out.
                    <span className="text-orange-400 ml-1">*Required</span>
                  </Label>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white min-h-[48px] text-base"
                  disabled={loading || !form.smsOptIn}
                  data-testid="owner-signup-submit"
                >
                  {loading ? 'Creating Account...' : 'Start Free Trial'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
              
              <p className="text-xs sm:text-sm text-slate-400 mt-4 text-center">
                By signing up, you agree to our{' '}
                <Link to="/terms" className="text-orange-400 hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-orange-400 hover:underline">Privacy Policy</Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
